// src/pages/admin/AdminDashboard.js
// Tableau de bord Admin unifié (onglets : Vue d'ensemble / Marketplace / SaaS).
// Maxula
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import StaffSalesPanel from '../../components/admin/StaffSalesPanel';
import StaffPerformancePanel from '../../components/admin/StaffPerformancePanel';
import { useAuth } from '../../context/AuthContext';
import {
  FiTrendingUp,
  FiUsers,
  FiShoppingBag,
  FiDollarSign,
  FiPackage,
  FiBarChart2,
  FiRepeat,
  FiGrid
} from 'react-icons/fi';
import API, { subscriptionAPI } from '../../services/api';
import normalizeImagePath, { getPlaceholderImage } from '../../utils/imageUtils';
import AdminSaaS from './AdminSaaS';

const safeNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const formatCurrency = (v) => {
  try { return safeNumber(v).toLocaleString('fr-FR'); } catch (e) { return String(v || 0); }
};

const StatCard = ({ icon: Icon, title, value, growth, color }) => (
  <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        {growth !== undefined && (
          <p className={`text-sm mt-2 flex items-center ${growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            <FiTrendingUp className="mr-1" />
            {growth >= 0 ? '+' : ''}{growth}% ce mois
          </p>
        )}
      </div>
      <div className={`p-4 rounded-full ${color}`}>
        <Icon className="text-white text-2xl" />
      </div>
    </div>
  </div>
);

const AdminDashboard = ({ initialTab = 'overview' }) => {
  const [tab, setTab] = useState(initialTab);

  const [stats, setStats] = useState({
    totalUsers: 0, totalMerchants: 0, totalDeals: 0, activeDeals: 0,
    totalRevenue: 0, monthlyRevenue: 0, topDeals: []
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [saas, setSaas] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    fetchDashboardData();
    fetchSaas();
    return () => { mountedRef.current = false; };
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const res = await API.get('/admin/dashboard');
      const data = res?.data;
      if (mountedRef.current && data && (data.success || data.data)) {
        setStats(data.data || data);
      }
      try {
        const ordersRes = await API.get('/admin/orders', { params: { limit: 5, sort: '-createdAt' } });
        const ordersData = ordersRes?.data;
        if (mountedRef.current && ordersData) {
          const arr = Array.isArray(ordersData.data) ? ordersData.data : Array.isArray(ordersData) ? ordersData : [];
          setRecentOrders(arr.slice(0, 5));
        }
      } catch (ordersErr) {
        if (mountedRef.current) setRecentOrders([]);
      }
    } catch (err) {
      /* silencieux : l'UI affiche des valeurs par défaut */
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const fetchSaas = async () => {
    try {
      const res = await subscriptionAPI.saasStats();
      if (mountedRef.current) setSaas(res.data?.data || null);
    } catch (e) {
      if (mountedRef.current) setSaas(null);
    }
  };

  const getDealPrice = (deal) => {
    if (!deal) return 0;
    const raw = deal.promoPrice ?? deal.discountedPrice ?? deal.discountPrice ??
      deal.currentPrice ?? deal.price ?? deal.originalPrice ?? 0;
    return safeNumber(raw);
  };

  const getOrderStatusBadge = (status) => {
    const styles = { completed: 'bg-green-100 text-green-800', pending: 'bg-yellow-100 text-yellow-800', cancelled: 'bg-red-100 text-red-800' };
    const labels = { completed: 'Complétée', pending: 'En attente', cancelled: 'Annulée' };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || (status ? String(status) : '—')}
      </span>
    );
  };

  const safeStats = {
    totalRevenue: safeNumber(stats.totalRevenue),
    monthlyRevenue: safeNumber(stats.monthlyRevenue),
    totalUsers: safeNumber(stats.totalUsers),
    totalMerchants: safeNumber(stats.totalMerchants),
    totalDeals: safeNumber(stats.totalDeals),
    activeDeals: safeNumber(stats.activeDeals),
    topDeals: Array.isArray(stats.topDeals) ? stats.topDeals : []
  };

  const tabs = [
    { key: 'overview', label: "Vue d'ensemble", icon: FiGrid },
    { key: 'marketplace', label: 'Marketplace', icon: FiShoppingBag },
    { key: 'saas', label: 'Revenus', icon: FiRepeat }
  ];

  // PERIMETRE DU COMPTE.
  //
  // Un commercial voyait les chiffres de TOUTE la plateforme : revenu global,
  // abonnements, MRR, nombre d'utilisateurs, 846 deals actifs. Rien de tout
  // cela ne le concerne, et le chiffre d'affaires global au-dessus de son
  // propre encart preta confusion — il pouvait croire que 2 200 MAD etaient
  // les siens.
  //
  // Ces vignettes sont donc reservees a l'administrateur. Le panneau
  // « Mes ventes », lui, est deja limite au portefeuille par le serveur.
  const { user } = useAuth() || {};
  const isPlatformAdmin = String(user?.role || '') === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  // -------- VUE D'ENSEMBLE --------
  const Overview = () => (
    <>
      {/* « Mes ventes » : perimetre du compte connecte. Place EN TETE — pour un
          commercial, c'est la seule chose qu'il vient regarder, et les chiffres
          globaux de la plateforme en dessous ne le concernent pas. Le composant
          se retire de lui-meme si l'appel echoue. */}
      <div className="mb-8">
        <StaffSalesPanel />
      </div>

      {/* RENDEMENT DE L'EQUIPE — admin uniquement.
          Place juste apres « Mes ventes » : c'est la question que l'admin se
          pose en ouvrant son tableau de bord, avant les chiffres globaux.
          Le tableau donne, par commercial, ses ventes et surtout SA MARGE. */}
      {isPlatformAdmin && (
        <div className="mb-8">
          <StaffPerformancePanel />
        </div>
      )}

      {/* Les deux natures de revenu, clairement distinguées — ADMIN uniquement. */}
      {isPlatformAdmin && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-100 mb-1">Revenu transactionnel (ventes / GMV)</p>
              <h2 className="text-4xl font-bold">{formatCurrency(safeStats.totalRevenue)} MAD</h2>
              <p className="text-primary-100 text-sm mt-1">Ce mois : {formatCurrency(safeStats.monthlyRevenue)} MAD</p>
            </div>
            <FiShoppingBag className="text-5xl text-primary-200" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 mb-1">Revenu abonnements (SaaS)</p>
              <h2 className="text-4xl font-bold">{formatCurrency(saas?.totalRevenue)} MAD</h2>
              <p className="text-emerald-100 text-sm mt-1">MRR : {formatCurrency(saas?.mrr)} MAD</p>
            </div>
            <FiRepeat className="text-5xl text-emerald-200" />
          </div>
        </div>
      </div>
      )}

      {isPlatformAdmin && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard icon={FiTrendingUp} title="MRR (récurrent / mois)" value={`${formatCurrency(saas?.mrr)} MAD`} color="bg-pink-500" />
        <StatCard icon={FiRepeat} title="ARR (récurrent / an)" value={`${formatCurrency(saas?.arr)} MAD`} color="bg-blue-500" />
        <StatCard icon={FiUsers} title="Utilisateurs" value={formatCurrency(safeStats.totalUsers)} color="bg-purple-500" />
        <StatCard icon={FiPackage} title="Deals Actifs" value={formatCurrency(safeStats.activeDeals)} color="bg-orange-500" />
      </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/admin/deals" className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg p-6 hover:shadow-lg transition-all">
          <FiPackage className="text-3xl mb-3" />
          <h3 className="text-xl font-semibold mb-2">Gérer les Deals</h3>
          {/* Compteurs PLATEFORME. Un commercial n'a ni les 868 deals ni les
              15 marchands dans son perimetre : les afficher contredit le
              « Portefeuille de 10 marchands » juste au-dessus. La carte reste,
              seul le chiffre disparait — il garde l'acces, il perd un chiffre
              qui n'est pas le sien. */}
          <p className="text-primary-100">
            {isPlatformAdmin ? `${formatCurrency(safeStats.totalDeals)} deals au total` : 'Voir les annonces de mes marchands'}
          </p>
        </Link>
        <Link to="/admin/orders" className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-6 hover:shadow-lg transition-all">
          <FiShoppingBag className="text-3xl mb-3" />
          <h3 className="text-xl font-semibold mb-2">Gérer les Commandes</h3>
          <p className="text-blue-100">Voir toutes les commandes</p>
        </Link>
        <Link to="/admin/merchants" className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg p-6 hover:shadow-lg transition-all">
          <FiUsers className="text-3xl mb-3" />
          <h3 className="text-xl font-semibold mb-2">Gérer les Marchands</h3>
          <p className="text-purple-100">
            {isPlatformAdmin ? `${formatCurrency(safeStats.totalMerchants)} marchands` : 'Voir mon portefeuille'}
          </p>
        </Link>
      </div>
    </>
  );

  // -------- MARKETPLACE --------
  const Marketplace = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard icon={FiDollarSign} title="Revenu Total" value={`${formatCurrency(safeStats.totalRevenue)} MAD`} growth={15} color="bg-green-500" />
        <StatCard icon={FiUsers} title="Utilisateurs" value={formatCurrency(safeStats.totalUsers)} growth={8} color="bg-purple-500" />
        <StatCard icon={FiShoppingBag} title="Marchands" value={formatCurrency(safeStats.totalMerchants)} color="bg-blue-500" />
        <StatCard icon={FiPackage} title="Deals Actifs" value={formatCurrency(safeStats.activeDeals)} color="bg-orange-500" />
      </div>

      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-lg p-6 mb-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-primary-100 mb-1">Revenu ce mois</p>
            <h2 className="text-4xl font-bold">{formatCurrency(safeStats.monthlyRevenue)} MAD</h2>
          </div>
          <FiTrendingUp className="text-5xl text-primary-200" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Link to="/admin/deals" className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg p-6 hover:shadow-lg transition-all">
          <FiPackage className="text-3xl mb-3" />
          <h3 className="text-xl font-semibold mb-2">Gérer les Deals</h3>
          {/* Compteurs PLATEFORME. Un commercial n'a ni les 868 deals ni les
              15 marchands dans son perimetre : les afficher contredit le
              « Portefeuille de 10 marchands » juste au-dessus. La carte reste,
              seul le chiffre disparait — il garde l'acces, il perd un chiffre
              qui n'est pas le sien. */}
          <p className="text-primary-100">
            {isPlatformAdmin ? `${formatCurrency(safeStats.totalDeals)} deals au total` : 'Voir les annonces de mes marchands'}
          </p>
        </Link>
        <Link to="/admin/orders" className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-6 hover:shadow-lg transition-all">
          <FiShoppingBag className="text-3xl mb-3" />
          <h3 className="text-xl font-semibold mb-2">Gérer les Commandes</h3>
          <p className="text-blue-100">Voir toutes les commandes</p>
        </Link>
        <Link to="/admin/merchants" className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg p-6 hover:shadow-lg transition-all">
          <FiUsers className="text-3xl mb-3" />
          <h3 className="text-xl font-semibold mb-2">Gérer les Marchands</h3>
          <p className="text-purple-100">
            {isPlatformAdmin ? `${formatCurrency(safeStats.totalMerchants)} marchands` : 'Voir mon portefeuille'}
          </p>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Commandes Récentes</h2>
              <Link to="/admin/orders" className="text-primary-600 hover:text-primary-700 text-sm font-medium">Voir tout</Link>
            </div>
          </div>
          <div className="divide-y">
            {recentOrders.length > 0 ? (
              recentOrders.map((order) => {
                const id = order?._id || (order?.id ?? null);
                const shortId = id ? String(id).slice(-6) : '—';
                const num = order.orderNumber || shortId;
                const name = order?.userId
                  ? `${order.userId.firstName || ''} ${order.userId.lastName || ''}`.trim()
                  : (order?.guestName || order?.customer?.firstName || 'Client inconnu');
                const total = formatCurrency(order?.total ?? order?.amount ?? 0);
                const items = Array.isArray(order.items) ? order.items : [];
                const titleOf = (it) => it.deal?.title || it.title || it.dealTitle || 'Deal';
                const titles = items.map(titleOf);
                const dealLabel = titles.length
                  ? `${titles[0]}${titles.length > 1 ? ` +${titles.length - 1}` : ''}`
                  : `Commande #${num}`;
                return (
                  <Link
                    key={id || num}
                    to={`/admin/orders?order=${encodeURIComponent(num)}`}
                    className="block p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate" title={titles.join(', ')}>
                          {dealLabel}
                        </p>
                        <p className="text-xs text-gray-500 truncate">#{num} • {name || 'Client'}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-semibold text-gray-900">{total} MAD</p>
                        {getOrderStatusBadge(order?.paymentStatus || order?.status)}
                      </div>
                    </div>
                  </Link>
                );
              })
            ) : (
              <div className="p-8 text-center text-gray-500">
                <FiShoppingBag className="mx-auto text-4xl mb-2" />
                <p>Aucune commande récente</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Top Deals</h2>
              <Link to="/admin/deals" className="text-primary-600 hover:text-primary-700 text-sm font-medium">Voir tout</Link>
            </div>
          </div>
          <div className="divide-y">
            {safeStats.topDeals.length > 0 ? (
              safeStats.topDeals.map((deal, index) => {
                const id = deal?._id || (deal?.id ?? index);
                const title = deal?.title || 'Untitled deal';
                const sold = safeNumber(deal?.quantitySold ?? deal?.soldCount ?? 0);
                const merchant = deal?.merchantId?.businessName || deal?.merchantName || 'N/A';
                const price = formatCurrency(getDealPrice(deal));
                const rawImage = deal.mainImage || deal.coverImage || (Array.isArray(deal.images) && deal.images[0]) || deal.image || null;
                const imageUrl = rawImage ? normalizeImagePath(rawImage) : getPlaceholderImage(80, 80, title);
                return (
                  <Link key={id} to={`/admin/deals?q=${encodeURIComponent(title)}`} className="block p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center font-bold text-pink-600">#{index + 1}</div>
                      <div className="flex-shrink-0">
                        <img src={imageUrl} alt={title} className="w-14 h-14 rounded-lg object-cover border border-gray-200" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{title}</p>
                        <p className="text-sm text-gray-600">{sold} vendus • {merchant}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{price} MAD</p>
                      </div>
                    </div>
                  </Link>
                );
              })
            ) : (
              <div className="p-8 text-center text-gray-500">
                <FiPackage className="mx-auto text-4xl mb-2" />
                <p>Aucun deal disponible</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <FiBarChart2 className="mr-2" />
          Statistiques des Deals
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-3xl font-bold text-blue-600">{formatCurrency(safeStats.totalDeals)}</p>
            <p className="text-sm text-gray-600 mt-1">Total Deals</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-3xl font-bold text-green-600">{formatCurrency(safeStats.activeDeals)}</p>
            <p className="text-sm text-gray-600 mt-1">Deals Actifs</p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <p className="text-3xl font-bold text-orange-600">{formatCurrency(safeStats.totalDeals - safeStats.activeDeals)}</p>
            <p className="text-sm text-gray-600 mt-1">Deals Inactifs</p>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Tableau de bord Admin</h1>
          <p className="text-gray-600 mt-1">Vue d&apos;ensemble de votre plateforme Maxula</p>

          {/* Onglets */}
          <div className="mt-5 flex flex-wrap gap-2 border-b -mb-px">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    active
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {tab === 'overview' && <Overview />}
        {tab === 'marketplace' && <Marketplace />}
        {tab === 'saas' && <AdminSaaS embedded />}
      </div>
    </div>
  );
};

export default AdminDashboard;
